import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload, Sparkles, Loader2, Wand2, Download, RefreshCw, Image as ImageIcon,
  Film, X, Check, AlertCircle, Cpu, ZoomIn, Camera, Zap, Eye,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ANON_KEY = "studio_anon_id";

const SOCIAL_FORMATS = [
  { id: "", label: "Original", w: "auto" },
  { id: "instagram", label: "Instagram 1:1", w: "1:1" },
  { id: "tiktok", label: "TikTok / Reels 9:16", w: "9:16" },
  { id: "story", label: "Story 9:16", w: "9:16" },
  { id: "youtube", label: "YouTube 16:9", w: "16:9" },
  { id: "twitter", label: "Twitter / X", w: "16:9" },
];

const PHASE2_LOCKED = [
  { label: "Real-ESRGAN 4× upscale", note: "Requires GPU cloud (Phase 2)" },
  { label: "GFPGAN face restoration", note: "Requires GPU cloud (Phase 2)" },
  { label: "Stable Video Diffusion frame interpolation", note: "Requires GPU cloud (Phase 2)" },
];

export default function MediaStudioPage() {
  const [presets, setPresets] = useState([]);
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [previewMode, setPreviewMode] = useState("after"); // "before" | "after" | "split"
  const [uploadProgress, setUploadProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [pickedPreset, setPickedPreset] = useState(null);
  const [socialFormat, setSocialFormat] = useState("");
  const [watermark, setWatermark] = useState(true);
  const [autoPick, setAutoPick] = useState(true);
  const [retouchStrength, setRetouchStrength] = useState("standard"); // 'subtle' | 'standard' | 'bold'
  const [dragOver, setDragOver] = useState(false);
  const [splitPos, setSplitPos] = useState(50);
  const fileInput = useRef(null);
  const pollTimer = useRef(null);

  // Anonymous user id — persisted in localStorage so a single browser
  // session sees its own jobs without needing to sign in. Held as
  // state so the polling effect re-fires the moment upload assigns
  // a fresh anon_id.
  const [anonId, setAnonIdState] = useState(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ANON_KEY);
  });

  const setAnonId = (id) => {
    if (id && typeof window !== "undefined") {
      localStorage.setItem(ANON_KEY, id);
      setAnonIdState(id);
    }
  };

  // Load presets on mount
  useEffect(() => {
    axios.get(`${API}/studio/presets`)
      .then((r) => setPresets(r.data?.items || []))
      .catch(() => toast.error("Couldn't load presets"));
  }, []);

  // Poll the job while it's processing
  useEffect(() => {
    if (!job || job.status === "complete" || job.status === "failed") {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
      return;
    }
    pollTimer.current = setInterval(async () => {
      try {
        const headers = anonId ? { "x-studio-anon": anonId } : {};
        const r = await axios.get(`${API}/studio/jobs/${job.id}`, { headers });
        setJob(r.data);
        if (r.data.status === "complete") {
          toast.success(`Enhanced in ${r.data.duration_ms || 0}ms · ${r.data.preset_id}`);
        } else if (r.data.status === "failed") {
          toast.error(r.data.error || "Processing failed");
        }
      } catch (err) { /* ignore */ }
    }, 2000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [job?.id, job?.status, anonId]);

  const onPick = (f) => {
    if (!f) return;
    const ext = f.name.toLowerCase().split(".").pop();
    const photoExts = ["jpg", "jpeg", "png", "webp", "heic", "heif", "tif", "tiff", "cr2", "cr3", "nef", "arw", "dng", "raf"];
    const videoExts = ["mp4", "mov", "m4v", "webm", "mkv", "avi"];
    if (![...photoExts, ...videoExts].includes(ext)) {
      toast.error(`Unsupported file type: .${ext}`);
      return;
    }
    setFile(f);
    setJob(null);
    setPickedPreset(null);
    setSplitPos(50);
  };

  const upload = async () => {
    if (!file) { toast.error("Pick a file first"); return; }
    setBusy(true); setUploadProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      if (pickedPreset) form.append("preset_id", pickedPreset);
      if (socialFormat) form.append("social_format", socialFormat);
      form.append("watermark", watermark ? "1" : "0");
      form.append("auto_pick", autoPick && !pickedPreset ? "1" : "0");
      form.append("retouch_strength", retouchStrength);
      const headers = { "Content-Type": "multipart/form-data" };
      if (anonId) headers["x-studio-anon"] = anonId;
      const r = await axios.post(`${API}/studio/upload`, form, {
        headers,
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      if (r.data.anon_id) setAnonId(r.data.anon_id);
      setJob(r.data.job);
      toast.success("Uploaded — AI is enhancing your media");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const reprocess = async (newPresetId) => {
    if (!job) return;
    setPickedPreset(newPresetId);
    setBusy(true);
    try {
      const form = new FormData();
      form.append("preset_id", newPresetId);
      if (socialFormat) form.append("social_format", socialFormat);
      form.append("watermark", watermark ? "1" : "0");
      form.append("retouch_strength", retouchStrength);
      const headers = anonId ? { "x-studio-anon": anonId } : {};
      await axios.post(`${API}/studio/jobs/${job.id}/reprocess`, form, { headers });
      // Refresh
      const r = await axios.get(`${API}/studio/jobs/${job.id}`, { headers });
      setJob(r.data);
    } catch (err) {
      toast.error("Re-process failed");
    } finally { setBusy(false); }
  };

  const downloadAfter = async () => {
    if (!job?.after_path) return;
    const headers = anonId ? { "x-studio-anon": anonId } : {};
    const r = await axios.get(`${API}/studio/jobs/${job.id}/after`, {
      headers, responseType: "blob",
    });
    const ext = job.kind === "video" ? "mp4" : "jpg";
    const blob = new Blob([r.data], { type: r.headers["content-type"] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `aristocratsja-studio-${job.id.substring(0, 8)}.${ext}`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    toast.success("Downloaded");
  };

  const reset = () => {
    setFile(null); setJob(null); setPickedPreset(null); setSplitPos(50);
    if (fileInput.current) fileInput.current.value = "";
  };

  const beforeUrl = job?.before_path
    ? `${API}/studio/jobs/${job.id}/before${anonId ? `?_a=${anonId}` : ""}`
    : null;
  const afterUrl = job?.after_path
    ? `${API}/studio/jobs/${job.id}/after${anonId ? `?_a=${anonId}` : ""}`
    : null;

  return (
    <div className="min-h-screen bg-luxury-black text-white pt-24 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/30 bg-gold/[0.05] mb-4">
            <Sparkles size={12} className="text-gold" />
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-body">
              AI Media Studio · Cinematic Auto-Enhance
            </p>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-3">
            Photo or video. <span className="text-gold italic">Studio quality.</span>
          </h1>
          <p className="text-white/55 font-body text-sm sm:text-base max-w-2xl mx-auto">
            Upload any photo or video — our AI auto-corrects color, lighting,
            sharpness, and (for video) stabilises shaky footage. Watermarked
            output in under 60 seconds. <span className="text-gold">Free. No signup needed.</span>
          </p>
        </motion.div>

        {/* No job yet — show upload zone */}
        {!job && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            data-testid="studio-upload-zone"
            className="max-w-3xl mx-auto"
          >
            <label
              onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(false);
                onPick(e.dataTransfer.files?.[0]);
              }}
              className={`block border-2 border-dashed rounded-xl py-12 px-8 text-center cursor-pointer transition-colors ${
                dragOver ? "border-gold bg-gold/[0.06]" : "border-white/15 hover:border-gold/40 bg-black/30"
              }`}>
              <input ref={fileInput} type="file"
                accept="image/*,video/*,.cr2,.cr3,.nef,.arw,.dng,.raf"
                onChange={(e) => onPick(e.target.files?.[0])}
                className="hidden"
                data-testid="studio-file-input" />
              <div className="flex flex-col items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gold/30 to-gold/5 border border-gold/30 flex items-center justify-center">
                  <Upload size={22} className="text-gold" />
                </div>
                <p className="text-lg font-heading text-white">Drag a file here, or pick one</p>
                {/* AI Retouch strength selector — three pills the user can pick before uploading */}
                <div data-testid="studio-retouch-strength" className="flex items-center gap-2 my-1">
                  <span className="text-[10px] font-body uppercase tracking-[0.18em] text-white/40 mr-1">AI Retouch:</span>
                  {[
                    { id: "subtle", label: "Subtle" },
                    { id: "standard", label: "Standard" },
                    { id: "bold", label: "Bold" },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      data-testid={`retouch-strength-${opt.id}`}
                      onClick={(e) => { e.preventDefault(); setRetouchStrength(opt.id); }}
                      className={`px-3 py-1 text-[11px] font-body uppercase tracking-wider border transition-colors ${
                        retouchStrength === opt.id
                          ? "bg-gold/20 border-gold/60 text-gold"
                          : "bg-white/5 border-white/15 text-white/60 hover:text-white hover:border-white/30"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {/* Explicit picker buttons — makes it obvious BOTH photos and videos work */}
                <div className="flex flex-wrap items-center justify-center gap-3 my-1">
                  <button
                    type="button"
                    data-testid="studio-pick-photo"
                    onClick={(e) => {
                      e.preventDefault();
                      if (fileInput.current) {
                        fileInput.current.accept = "image/*,.cr2,.cr3,.nef,.arw,.dng,.raf";
                        fileInput.current.click();
                      }
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold/15 hover:bg-gold/25 border border-gold/40 text-gold text-xs font-body uppercase tracking-wider transition-colors"
                  >
                    <ImageIcon size={14} /> Pick a photo
                  </button>
                  <button
                    type="button"
                    data-testid="studio-pick-video"
                    onClick={(e) => {
                      e.preventDefault();
                      if (fileInput.current) {
                        fileInput.current.accept = "video/*";
                        fileInput.current.click();
                      }
                    }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-gold/40 text-white text-xs font-body uppercase tracking-wider transition-colors"
                  >
                    <Film size={14} /> Pick a video
                  </button>
                </div>
                <p className="text-[11px] text-white/40 max-w-md">
                  JPEG · PNG · HEIC · RAW (CR2/NEF/ARW/DNG) · MP4 · MOV — up to 60 MB for photos, 500 MB for video
                </p>
                {file && (
                  <div data-testid="studio-picked-file" className="mt-2 flex items-center gap-2 px-3 py-2 bg-gold/10 border border-gold/30 rounded">
                    {file.type.startsWith("video") ? <Film size={14} className="text-gold" /> : <ImageIcon size={14} className="text-gold" />}
                    <p className="text-sm text-white">{file.name}</p>
                    <span className="text-[10px] text-white/45">
                      {(file.size / (1024 * 1024)).toFixed(1)} MB
                    </span>
                    <button onClick={(e) => { e.preventDefault(); reset(); }} className="text-white/40 hover:text-rose-300">
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>
            </label>

            {file && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="mt-6 space-y-4">
                {/* Auto-pick toggle */}
                <label className="flex items-center justify-center gap-2 text-sm text-white/65 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoPick}
                    onChange={(e) => setAutoPick(e.target.checked)}
                    className="accent-gold-500"
                    data-testid="studio-auto-pick"
                  />
                  <Wand2 size={14} className="text-gold" />
                  <span>Let GPT-5.2 vision pick the perfect cinematic look</span>
                </label>

                {/* Manual preset picker (collapsed unless autoPick=false) */}
                {!autoPick && (
                  <PresetGrid presets={presets} picked={pickedPreset} onPick={setPickedPreset} />
                )}

                {/* Social format + watermark */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gold mb-1.5">Social-media format</p>
                    <select value={socialFormat} onChange={(e) => setSocialFormat(e.target.value)}
                      data-testid="studio-social-format"
                      className="w-full bg-black/40 border border-white/15 rounded px-3 py-2.5 text-sm text-white">
                      {SOCIAL_FORMATS.map((f) => (<option key={f.id} value={f.id}>{f.label}</option>))}
                    </select>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gold mb-1.5">Watermark</p>
                    <button
                      data-testid="studio-watermark-toggle"
                      onClick={() => setWatermark(!watermark)}
                      className={`w-full px-3 py-2.5 border rounded text-sm transition-colors ${
                        watermark
                          ? "border-gold/60 bg-gold/15 text-gold"
                          : "border-white/15 text-white/55 hover:text-white"
                      }`}>
                      {watermark ? "✓ AristocratsJA logo will be stamped" : "No watermark"}
                    </button>
                  </div>
                </div>

                <button
                  data-testid="studio-enhance-btn"
                  onClick={upload}
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 py-4 bg-gold text-luxury-black text-sm uppercase tracking-wider rounded font-medium hover:bg-gold/90 transition-colors disabled:opacity-50">
                  {busy
                    ? <><Loader2 size={16} className="animate-spin" /> Uploading {uploadProgress}%</>
                    : <><Sparkles size={16} /> Enhance with AI</>}
                </button>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Job state — processing or complete */}
        {job && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            data-testid="studio-job-pane"
            className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6"
          >
            {/* PREVIEW */}
            <div className="space-y-4">
              {/* Mode tabs */}
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  {[
                    { id: "before", label: "Before", icon: ImageIcon },
                    { id: "after", label: "After", icon: Sparkles },
                    { id: "split", label: "Split", icon: ZoomIn },
                  ].map((m) => (
                    <button key={m.id} onClick={() => setPreviewMode(m.id)}
                      data-testid={`studio-mode-${m.id}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider rounded transition-colors ${
                        previewMode === m.id
                          ? "bg-gold/15 text-gold border border-gold/40"
                          : "text-white/55 hover:text-white border border-white/10"
                      }`}>
                      <m.icon size={10} /> {m.label}
                    </button>
                  ))}
                </div>
                <button onClick={reset} data-testid="studio-new-upload"
                  className="text-[10px] uppercase tracking-wider text-white/45 hover:text-gold inline-flex items-center gap-1">
                  <RefreshCw size={10} /> New upload
                </button>
              </div>

              {/* Preview pane */}
              <div className="relative rounded-lg overflow-hidden border border-white/10 bg-black aspect-video"
                onMouseMove={(e) => {
                  if (previewMode !== "split") return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  setSplitPos(Math.max(0, Math.min(100,
                    ((e.clientX - rect.left) / rect.width) * 100)));
                }}>
                {job.status !== "complete" && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-luxury-black/80 z-10">
                    <Loader2 size={36} className="animate-spin text-gold" />
                    <p className="text-[11px] uppercase tracking-wider text-gold">
                      {job.status === "processing"
                        ? `Processing ${job.progress_pct || 0}%`
                        : job.status === "failed"
                          ? "Failed — try a different file"
                          : "Queued..."}
                    </p>
                    {job.status === "processing" && (
                      <div className="w-48 h-1 bg-black/60 rounded-full overflow-hidden">
                        <div className="h-full bg-gold transition-all"
                          style={{ width: `${job.progress_pct || 0}%` }} />
                      </div>
                    )}
                    {job.error && (
                      <p className="text-[10px] text-rose-300/80 max-w-md text-center">{job.error}</p>
                    )}
                  </div>
                )}
                {/* The actual media (after / before / split) */}
                {job.kind === "photo" ? (
                  <>
                    {(previewMode === "before" || previewMode === "split") && beforeUrl && (
                      <img src={beforeUrl} alt="Before" data-testid="studio-img-before"
                        className="absolute inset-0 w-full h-full object-contain"
                        style={previewMode === "split" ? {
                          clipPath: `inset(0 ${100 - splitPos}% 0 0)`,
                        } : {}} />
                    )}
                    {(previewMode === "after" || previewMode === "split") && afterUrl && (
                      <img src={afterUrl} alt="After" data-testid="studio-img-after"
                        className="absolute inset-0 w-full h-full object-contain"
                        style={previewMode === "split" ? {
                          clipPath: `inset(0 0 0 ${splitPos}%)`,
                        } : {}} />
                    )}
                    {previewMode === "split" && (
                      <div className="absolute top-0 bottom-0 w-0.5 bg-gold pointer-events-none"
                        style={{ left: `${splitPos}%` }} />
                    )}
                  </>
                ) : (
                  <video controls src={previewMode === "before" ? beforeUrl : afterUrl}
                    poster={job.poster_path ? `${API}/studio/jobs/${job.id}/poster${anonId ? `?_a=${anonId}` : ""}` : undefined}
                    data-testid="studio-video"
                    className="absolute inset-0 w-full h-full object-contain bg-black" />
                )}
              </div>

              {/* Action buttons */}
              {job.status === "complete" && (
                <div className="flex gap-2">
                  <button
                    data-testid="studio-download"
                    onClick={downloadAfter}
                    className="flex-1 inline-flex items-center justify-center gap-2 py-3 bg-gold text-luxury-black text-sm uppercase tracking-wider rounded font-medium hover:bg-gold/90 transition-colors">
                    <Download size={14} /> Download enhanced
                  </button>
                </div>
              )}
            </div>

            {/* SIDEBAR */}
            <div className="space-y-4">
              <AnalysisCard job={job} />
              <PresetSidebar
                presets={presets}
                pickedPreset={job.preset_id}
                onPick={reprocess}
                busy={busy || (job.status && job.status !== "complete")}
              />
              <Phase2Card />
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

function PresetGrid({ presets, picked, onPick }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-gold mb-2">Pick a cinematic look ({presets.length})</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-y-auto pr-1">
        {presets.map((p) => (
          <button key={p.id} onClick={() => onPick(p.id)}
            data-testid={`studio-preset-${p.id}`}
            className={`text-left p-2.5 rounded border transition-colors ${
              picked === p.id
                ? "border-gold bg-gold/[0.08] text-gold"
                : "border-white/10 hover:border-gold/30 text-white/75"
            }`}>
            <p className="text-[11px] font-medium">{p.label}</p>
            <p className="text-[9px] opacity-65 mt-1 line-clamp-2">{p.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function PresetSidebar({ presets, pickedPreset, onPick, busy }) {
  return (
    <div className="p-4 border border-white/10 bg-black/30 rounded-md">
      <p className="text-[10px] uppercase tracking-wider text-gold mb-2 inline-flex items-center gap-1.5">
        <Wand2 size={11} /> Cinematic looks
      </p>
      <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
        {presets.map((p) => (
          <button key={p.id} onClick={() => !busy && onPick(p.id)}
            disabled={busy}
            data-testid={`studio-preset-sb-${p.id}`}
            className={`w-full text-left p-2 rounded border text-[10px] transition-colors disabled:opacity-40 ${
              pickedPreset === p.id
                ? "border-gold bg-gold/[0.1] text-gold"
                : "border-white/8 hover:border-gold/30 text-white/75"
            }`}>
            <p className="font-medium">{p.label}</p>
            <p className="opacity-60 mt-0.5 line-clamp-1">{p.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

function AnalysisCard({ job }) {
  const a = job.analysis;
  if (!a) return null;
  return (
    <div data-testid="studio-analysis" className="p-4 border border-white/10 bg-black/30 rounded-md space-y-2">
      <p className="text-[10px] uppercase tracking-wider text-gold inline-flex items-center gap-1.5">
        <Eye size={11} /> AI analysis
      </p>
      <dl className="grid grid-cols-2 gap-y-1 text-[10px]">
        <dt className="text-white/45">Scene</dt>
        <dd className="text-gold font-medium text-right">{a.scene}</dd>
        {a.face_count != null && (<>
          <dt className="text-white/45">Faces detected</dt>
          <dd className="text-white text-right">{a.face_count}</dd>
        </>)}
        {a.width && (<>
          <dt className="text-white/45">Resolution</dt>
          <dd className="text-white text-right">{a.width} × {a.height}</dd>
        </>)}
        {a.brightness != null && (<>
          <dt className="text-white/45">Brightness</dt>
          <dd className="text-white text-right">{a.brightness}</dd>
        </>)}
        {a.contrast != null && (<>
          <dt className="text-white/45">Contrast</dt>
          <dd className="text-white text-right">{a.contrast}</dd>
        </>)}
        {a.duration_seconds && (<>
          <dt className="text-white/45">Duration</dt>
          <dd className="text-white text-right">{a.duration_seconds.toFixed(1)}s</dd>
        </>)}
      </dl>
      {a.notes?.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {a.notes.map((n) => (
            <span key={n} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60">{n}</span>
          ))}
        </div>
      )}
      {job.applied_steps?.length > 0 && (
        <div className="pt-2 border-t border-white/5">
          <p className="text-[9px] uppercase tracking-wider text-white/40 mb-1">Pipeline ({job.applied_steps.length} steps · {job.duration_ms}ms)</p>
          <div className="flex flex-wrap gap-1">
            {job.applied_steps.map((s, i) => (
              <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/[0.08] border border-emerald-400/20 text-emerald-300/80">
                <Check size={8} className="inline -mt-0.5" /> {s}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Phase2Card() {
  return (
    <div className="p-4 border border-amber-400/25 bg-amber-500/[0.04] rounded-md">
      <p className="text-[10px] uppercase tracking-wider text-amber-300 inline-flex items-center gap-1.5 mb-2">
        <Cpu size={11} /> Phase 2 — GPU unlock
      </p>
      <ul className="space-y-1.5">
        {PHASE2_LOCKED.map((p) => (
          <li key={p.label} className="flex items-center justify-between text-[10px]">
            <span className="text-white/75">{p.label}</span>
            <span className="text-amber-300/60 text-[9px]">{p.note}</span>
          </li>
        ))}
      </ul>
      <p className="text-[9px] text-white/35 mt-2">
        Unlocks Real-ESRGAN 4× upscale, GFPGAN face restoration, SVD video frame interpolation when a CUDA worker is attached.
      </p>
    </div>
  );
}
